import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Contact, Plus, Loader2, Link2, Mail, Trash2, Target, TrendingUp, FileText, Receipt, CreditCard, LifeBuoy, History } from "lucide-react";

const EVENT_META = {
  lead: { icon: Target, tone: "text-info bg-info/10" },
  deal: { icon: TrendingUp, tone: "text-primary bg-primary/10" },
  quote: { icon: FileText, tone: "text-automation bg-automation/10" },
  invoice: { icon: Receipt, tone: "text-warning bg-warning/10" },
  payment: { icon: CreditCard, tone: "text-success bg-success/10" },
  ticket: { icon: LifeBuoy, tone: "text-escalation bg-escalation/10" },
};

function initials(name) {
  return (name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function Timeline({ customerId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["customer-timeline", customerId],
    queryFn: async () => (await api.get(`/customers/${customerId}/timeline`)).data,
    enabled: !!customerId,
  });

  if (isLoading || !data) return <div className="mt-4"><Loading rows={4} /></div>;
  const { customer, stats, timeline } = data;

  const chips = [
    { label: "Invoiced", value: money(stats.total_invoiced) },
    { label: "Collected", value: money(stats.total_paid) },
    { label: "Open deals", value: stats.open_deals },
    { label: "Open tickets", value: stats.open_tickets },
    { label: "Quotes", value: stats.quotes },
    { label: "Leads", value: stats.leads },
  ];

  return (
    <div className="mt-2">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-base font-semibold text-primary">{initials(customer.name)}</span>
        <div className="min-w-0">
          <p className="truncate font-heading text-lg font-bold">{customer.name}</p>
          <p className="truncate text-sm text-muted-foreground">{customer.company} · {customer.email}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {chips.map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-card p-2.5 text-center" data-testid={`c360-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-heading text-base font-bold text-foreground">{c.value}</p>
            <p className="text-[11px] text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <p className="mb-2 mt-6 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <History className="h-3.5 w-3.5" /> Activity timeline
      </p>
      {timeline.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">No activity recorded for this customer yet.</p>
      ) : (
        <div className="relative space-y-3 pl-2" data-testid="c360-timeline">
          {timeline.map((e, i) => {
            const meta = EVENT_META[e.type] || EVENT_META.lead;
            const Icon = meta.icon;
            return (
              <div key={`${e.type}-${e.id}-${i}`} className="flex gap-3" data-testid={`c360-event-${e.type}-${e.id}`}>
                <div className="flex flex-col items-center">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tone}`}><Icon className="h-4 w-4" /></span>
                  {i < timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium leading-tight">{e.title}</p>
                    {typeof e.amount === "number" && e.amount > 0 && <span className="shrink-0 font-heading text-sm font-semibold text-foreground">{money(e.amount)}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {e.status && <StatusBadge value={e.status} />}
                    {e.subtitle && <span className="text-xs text-muted-foreground">{e.subtitle}</span>}
                    <span className="text-xs text-muted-foreground">{e.date ? new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Customers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [f, setF] = useState({ name: "", email: "", company: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: customers = [], isLoading } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/customers", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); setOpen(false); setF({ name: "", email: "", company: "" }); toast.success("Portal customer created"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to create customer"),
  });
  const linkMut = useMutation({
    mutationFn: (id) => api.post(`/customers/${id}/magic-link`),
    onSuccess: async (res) => {
      const url = `${window.location.origin}/portal/login?token=${res.data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Magic link copied to clipboard", { description: url }); }
      catch { toast.success("Magic link generated", { description: url }); }
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/customers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); toast.success("Customer removed & links revoked"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to remove customer"),
  });

  return (
    <div>
      <PageHeader title="Customer Portal Accounts" subtitle="Give customers secure access to invoices, quotes & tickets" testid="customers-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-customer-btn"><Plus className="mr-1.5 h-4 w-4" /> New Customer</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create portal customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Contact name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="customer-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="customer-email-input" /></div>
              <div className="space-y-1.5"><Label>Company</Label><Input value={f.company} onChange={(e) => set("company", e.target.value)} placeholder="Links portal to this customer's records" data-testid="customer-company-input" /></div>
              <DialogFooter><Button onClick={() => createMut.mutate(f)} disabled={createMut.isPending || !f.name || !f.email} data-testid="customer-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <Loading /> : customers.length === 0 ? (
        <EmptyState icon={Contact} title="No portal customers yet" description="Create a customer account, then share a secure magic link so they can pay invoices and raise tickets."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Customer</Button>} testid="customers-empty" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {customers.map((c) => (
            <Card key={c.id} className="cursor-pointer p-4 transition-colors hover:border-primary/40" data-testid={`customer-${c.id}`} onClick={() => setViewing(c)}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">{initials(c.name)}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{c.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.company}</p>
                </div>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="h-3.5 w-3.5" /> {c.email}</p>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={(e) => { e.stopPropagation(); setViewing(c); }} data-testid={`view-360-${c.id}`}>
                  <History className="mr-1.5 h-3.5 w-3.5" /> View 360
                </Button>
                <Button variant="ghost" size="icon" title="Copy portal link" onClick={(e) => { e.stopPropagation(); linkMut.mutate(c.id); }} disabled={linkMut.isPending} data-testid={`magic-link-${c.id}`}><Link2 className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteMut.mutate(c.id); }} data-testid={`remove-customer-${c.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="customer-360-drawer">
          <SheetHeader><SheetTitle>Customer 360</SheetTitle>
            <SheetDescription>Full relationship history: leads, deals, quotes, invoices, payments and tickets.</SheetDescription>
          </SheetHeader>
          {viewing && <Timeline customerId={viewing.id} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
